import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Loading } from "../components/Loader";
import PermissionMatrix from "../components/PermissionMatrix";
import RoleCheckboxList from "../components/RoleCheckboxList";
import {
  fetchAccessResources,
  fetchRoles,
  fetchUser,
  selectAccessResources,
  selectRoles,
  selectUser,
  setUserPassword,
  setUserPermissions,
  updateUser,
} from "../features/access/accessSlice";
import { matrixEquals } from "../features/access/matrix";
import { formatDateTime, fullName } from "../features/access/format";
import { fetchMe, selectAuth } from "../features/auth/authSlice";
import { can } from "../features/auth/permissions";
import "./ManagementPages.css";

function formFromUser(u) {
  return {
    first_name: u.first_name || "",
    last_name: u.last_name || "",
    email: u.email || "",
    is_active: u.is_active,
    is_staff: u.is_staff,
    roles: u.roles.map((r) => r.id),
  };
}

function sameRoles(a, b) {
  return JSON.stringify([...a.roles].sort()) === JSON.stringify([...b.roles].sort());
}

function sameForm(a, b) {
  return JSON.stringify({ ...a, roles: [...a.roles].sort() }) === JSON.stringify({ ...b, roles: [...b.roles].sort() });
}

/**
 * «Итоговые права» с личными исключениями: клик выдаёт или отнимает право только этому
 * человеку, роли не меняются. База для подсветки — права ролей (`role_permissions`).
 */
function UserPermissionsEditor({ target, registry, canEdit, rolesDirty, onSaved }) {
  const dispatch = useDispatch();
  const [draft, setDraft] = useState(target.permissions);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    setDraft(target.permissions);
  }, [target]);

  const dirty = !matrixEquals(draft, target.permissions);
  const differsFromRoles = !matrixEquals(draft, target.role_permissions);

  const save = async () => {
    setSaving(true);
    setMessage(null);
    const result = await dispatch(setUserPermissions({ id: target.id, permissions: draft }));
    setSaving(false);
    if (setUserPermissions.fulfilled.match(result)) {
      setMessage({ kind: "success", text: "Личные права сохранены." });
      onSaved();
    } else {
      setMessage({ kind: "error", text: result.payload });
    }
  };

  const onChange = (next) => {
    setDraft(next);
    setMessage(null);
  };

  return (
    <div>
      <div className="mg-card-title">Итоговые права</div>
      <div className="mg-subtitle mg-section-note">
        {target.is_superuser
          ? "Суперпользователь имеет все права независимо от ролей — личные исключения не нужны."
          : canEdit
            ? "Права ролей плюс личные исключения. Клик выдаёт или отнимает право только этому сотруднику — роль и другие сотрудники не меняются."
            : "Права ролей плюс личные исключения."}
        {rolesDirty && " Роли изменены, но не сохранены — матрица пока по сохранённым ролям."}
      </div>
      {message && (
        <div className={message.kind === "error" ? "mg-error mg-section-note" : "mg-success mg-section-note"}>
          {message.text}
        </div>
      )}
      <PermissionMatrix
        registry={registry}
        value={draft}
        baseline={target.is_superuser ? null : target.role_permissions}
        legend={{ added: "выдано лично, сверх ролей", removed: "отнято лично, хотя даёт роль" }}
        onChange={canEdit ? onChange : undefined}
      />
      {canEdit && (dirty || differsFromRoles) && (
        <div className="mg-savebar mg-savebar--spaced">
          <span className="mg-savebar-text">
            {dirty ? "Есть несохранённые изменения" : "У сотрудника есть личные исключения из прав ролей"}
          </span>
          <div className="mg-actions">
            {differsFromRoles && (
              <button
                type="button"
                className="mg-btn"
                disabled={saving}
                title="Убрать все личные исключения — останутся только права ролей"
                onClick={() => onChange(target.role_permissions)}
              >
                Сбросить к правам ролей
              </button>
            )}
            {dirty && (
              <>
                <button type="button" className="mg-btn" disabled={saving} onClick={() => onChange(target.permissions)}>
                  Отменить
                </button>
                <button type="button" className="mg-btn mg-btn--primary" disabled={saving} onClick={save}>
                  {saving ? "Сохраняем…" : "Сохранить личные права"}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Карточка пользователя: профиль, роли, активность, сброс пароля и итоговые права —
 * объединение по всем ролям, то есть то, что сотрудник реально увидит в системе.
 */
export default function UserEditPage() {
  const { userId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user: me } = useSelector(selectAuth);
  const userState = useSelector(selectUser);
  const roles = useSelector(selectRoles);
  const registry = useSelector(selectAccessResources);

  const target = userState.data && String(userState.data.id) === String(userId) ? userState.data : null;
  const isSelf = Boolean(target && me && target.id === me.id);
  // Суперпользователя правит только суперпользователь — это же проверяет бэкенд.
  const locked = Boolean(target?.is_superuser && !me?.is_superuser);
  const canWrite = can(me, "user", "write") && !locked;

  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [password, setPassword] = useState("");
  const [passwordMessage, setPasswordMessage] = useState(null);

  useEffect(() => {
    dispatch(fetchUser(userId));
  }, [dispatch, userId]);

  useEffect(() => {
    if (roles.status === "idle") dispatch(fetchRoles());
    if (registry.status === "idle") dispatch(fetchAccessResources());
  }, [dispatch, roles.status, registry.status]);

  useEffect(() => {
    if (target) setForm(formFromUser(target));
  }, [target]);

  const dirty = useMemo(() => Boolean(target && form && !sameForm(form, formFromUser(target))), [target, form]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    const result = await dispatch(updateUser({ id: target.id, ...form }));
    setSaving(false);
    if (updateUser.fulfilled.match(result)) {
      setMessage({ kind: "success", text: "Изменения сохранены." });
      if (isSelf) dispatch(fetchMe());
    } else {
      setMessage({ kind: "error", text: result.payload });
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPasswordMessage(null);
    const result = await dispatch(setUserPassword({ id: target.id, password }));
    if (setUserPassword.fulfilled.match(result)) {
      setPassword("");
      setPasswordMessage({ kind: "success", text: "Пароль изменён. Сообщите его сотруднику лично." });
    } else {
      setPasswordMessage({ kind: "error", text: result.payload });
    }
  };

  if (userState.status === "failed" && !target) {
    return (
      <div className="mgmt-page">
        <div className="mg-body">
          <div className="mg-error">{userState.error}</div>
        </div>
      </div>
    );
  }

  if (!target || !form) {
    return (
      <div className="mgmt-page">
        <Loading text="Загрузка пользователя…" />
      </div>
    );
  }

  const roleList = Array.isArray(roles.data) ? roles.data : [];

  return (
    <div className="mgmt-page">
      <div className="mg-header">
        <div>
          <button type="button" className="mg-back" onClick={() => navigate(-1)}>
            ← Пользователи
          </button>
          <div className="mg-title">
            {fullName(target) || target.username}
            <span className="mg-badge mg-mono">{target.username}</span>
            {target.is_superuser && <span className="mg-badge mg-badge--accent">суперпользователь</span>}
            {!target.is_active && <span className="mg-badge mg-badge--danger">отключён</span>}
          </div>
          <div className="mg-subtitle">
            Последний вход: {formatDateTime(target.last_login)} · В системе с {formatDateTime(target.date_joined)}
          </div>
        </div>
      </div>

      <div className="mg-body">
        {locked && <div className="mg-hint">Суперпользователя может изменять только суперпользователь.</div>}

        <div className="mg-columns">
          <form className="mg-card" onSubmit={save}>
            <div className="mg-card-title">Профиль и роли</div>
            <div className="mg-form">
              <label className="mg-field">
                <span className="mg-label">Фамилия</span>
                <input
                  className="mg-input"
                  value={form.last_name}
                  disabled={!canWrite}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                />
              </label>
              <label className="mg-field">
                <span className="mg-label">Имя</span>
                <input
                  className="mg-input"
                  value={form.first_name}
                  disabled={!canWrite}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                />
              </label>
              <label className="mg-field mg-field--wide">
                <span className="mg-label">Email</span>
                <input
                  className="mg-input"
                  type="email"
                  value={form.email}
                  disabled={!canWrite}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </label>
              <div className="mg-field mg-field--wide">
                <span className="mg-label">Роли</span>
                <RoleCheckboxList
                  roles={roleList}
                  value={form.roles}
                  disabled={!canWrite}
                  onChange={(ids) => setForm({ ...form, roles: ids })}
                />
              </div>
              <label className="mg-toggle mg-field--wide">
                <input
                  type="checkbox"
                  checked={form.is_staff}
                  disabled={!canWrite || isSelf}
                  onChange={(e) => setForm({ ...form, is_staff: e.target.checked })}
                />
                <span>
                  Доступ в CRM
                  <span className="mg-role-desc">
                    {isSelf
                      ? "Снять доступ в CRM с самого себя нельзя."
                      : "Без него сотрудник работает только на кассовом терминале."}
                  </span>
                </span>
              </label>
              <label className="mg-toggle mg-field--wide">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  disabled={!canWrite || isSelf}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                />
                <span>
                  Активен
                  <span className="mg-role-desc">
                    {isSelf
                      ? "Себя отключить нельзя."
                      : "Отключённый сотрудник не может войти ни в CRM, ни в кассу. История сохраняется."}
                  </span>
                </span>
              </label>
            </div>
            {message && <div className={message.kind === "error" ? "mg-error" : "mg-success"}>{message.text}</div>}
            {canWrite && (
              <div className="mg-form-actions">
                <button
                  type="button"
                  className="mg-btn"
                  disabled={!dirty || saving}
                  onClick={() => setForm(formFromUser(target))}
                >
                  Отменить
                </button>
                <button type="submit" className="mg-btn mg-btn--primary" disabled={!dirty || saving}>
                  {saving ? "Сохраняем…" : "Сохранить"}
                </button>
              </div>
            )}
          </form>

          {canWrite && (
            <form className="mg-card" onSubmit={changePassword}>
              <div className="mg-card-title">Сброс пароля</div>
              <div className="mg-form">
                <label className="mg-field mg-field--wide">
                  <span className="mg-label">Новый пароль</span>
                  <input
                    className="mg-input"
                    type="password"
                    value={password}
                    minLength={6}
                    autoComplete="new-password"
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <span className="mg-field-hint">Не меньше 6 символов.</span>
                </label>
              </div>
              {passwordMessage && (
                <div className={passwordMessage.kind === "error" ? "mg-error" : "mg-success"}>
                  {passwordMessage.text}
                </div>
              )}
              <div className="mg-form-actions">
                <button type="submit" className="mg-btn" disabled={password.length < 6}>
                  Сменить пароль
                </button>
              </div>
            </form>
          )}
        </div>

        {registry.data ? (
          <UserPermissionsEditor
            target={target}
            registry={registry.data}
            canEdit={canWrite && !target.is_superuser}
            rolesDirty={dirty && !sameRoles(form, formFromUser(target))}
            onSaved={() => isSelf && dispatch(fetchMe())}
          />
        ) : (
          <Loading text="Загрузка разделов…" />
        )}
      </div>
    </div>
  );
}
